import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import moment from "moment";
import {compose} from "mu-lambda";
import * as React from "react";
import {useEffect, useState} from "react";

import Banner from "@khanacademy/wonder-blocks-banner";
import {
    Breadcrumbs,
    BreadcrumbsItem,
} from "@khanacademy/wonder-blocks-breadcrumbs";
import Button from "@khanacademy/wonder-blocks-button";
import {useOnMountEffect, View} from "@khanacademy/wonder-blocks-core";
import {Checkbox} from "@khanacademy/wonder-blocks-form";
import {Spring, Strut} from "@khanacademy/wonder-blocks-layout";
import Link from "@khanacademy/wonder-blocks-link";
import {CircularSpinner} from "@khanacademy/wonder-blocks-progress-spinner";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {
    Body,
    HeadingLarge,
    HeadingMedium,
} from "@khanacademy/wonder-blocks-typography";
import {
    enums,
    usePublishAnalyticsEvent,
    withConversionContext,
    withMarkConversion,
    type WithMarkConversionProps,
    type WithoutMarkConversion,
} from "@repo/analytics-learning-time";
import {
    DateRangeDropdown,
    getSelectedDateRange,
    type DateRangeSelection,
} from "@repo/component-date-range-dropdown";
import {ErrorMessageWrapper} from "@repo/component-error-message";
import {ReportError} from "@repo/component-screens/error-loading";
import {useQuery} from "@repo/data-gql";
import {useLocation} from "@repo/urls";

import {useUpdatePageSettings} from "~/data-access/use-update-page-settings.ts";

import ConversationsView from "./conversations-view.tsx";
import {GUIDE_TRANSCRIPTS_QUERY} from "./guide-transcripts-query.ts";
import TableView from "./table-view.tsx";

// As designed
const DATE_FILTER_WIDTH = 288;
const FILTERS_MARGIN_TOP = 35;

interface OwnProps {
    kaid: string;
    profilePath?: string;
    relationship: "SELF" | "COACH" | "DISTRICT_ADMIN" | "OTHER";
    renderTableView?: boolean;
}

type Props = OwnProps & WithMarkConversionProps;

const DEFAULT_DATE_RANGE_VALUE = "last-30-days";

/**
 * Gets the correct ISO datetime strings for the given date range.
 *
 * For start dates, we modify the time to the start of the day, while for end
 * dates, we modify the time to the very end of the day before converting to
 * ISO format. Returns null values if there is not a valid date or date range
 * provided.
 */
const maybeGetDateISOStrings = (
    selection: DateRangeSelection,
): {startDate: string | null; endDate: string | null} => {
    const dateRange = selection.dateRange;
    const startDate = dateRange?.startDate
        ? moment(dateRange.startDate).startOf("day").toISOString()
        : null;
    const endDate = dateRange?.endDate
        ? moment(dateRange.endDate).endOf("day").toISOString()
        : null;
    return {startDate, endDate};
};

const GuideTranscripts = ({
    kaid,
    markConversion,
    profilePath,
    relationship,
    renderTableView,
}: Props): React.ReactElement => {
    useOnMountEffect(() => {
        markConversion({id: "guide_transcripts_page_view"});
    });
    useUpdatePageSettings({
        title: t`Khanmigo Chat History`,
    });
    const {
        queryParams: {
            cursor,
            selectFlaggedChats,
            startDate: queryParamsStartDate,
            endDate: queryParamsEndDate,
        },
        pathname,
    } = useLocation();

    const [dateRangeSelection, setDateRangeSelection] =
        useState<DateRangeSelection>(
            getSelectedDateRange(null, DEFAULT_DATE_RANGE_VALUE),
        );

    useEffect(() => {
        if (queryParamsStartDate && queryParamsEndDate) {
            setDateRangeSelection({
                selectedValue: "custom",
                dateRange: {
                    startDate: moment(queryParamsStartDate).toDate(),
                    endDate: moment(queryParamsEndDate).toDate(),
                },
            });
        }
    }, [queryParamsStartDate, queryParamsEndDate, setDateRangeSelection]);

    const {startDate, endDate} = maybeGetDateISOStrings(dateRangeSelection);

    const [flaggedThreadsOnly, setFlaggedThreadsOnly] =
        useState<boolean>(!!selectFlaggedChats);

    const [result] = useQuery(GUIDE_TRANSCRIPTS_QUERY, {
        variables: {
            kaid,
            cursor,
            startDate,
            endDate,
            moderationStatus: flaggedThreadsOnly ? "FLAGGED" : "ALL",
        },
    });
    const publishAnalyticsEvent = usePublishAnalyticsEvent();

    useEffect(() => {
        publishAnalyticsEvent({
            eventType: "AiGuideHistoryOpened",
            eventSchemaVersion: 3,
            reviewerRelationship: relationship,
        });

        return function cleanup() {
            publishAnalyticsEvent({
                eventType: "AiGuideHistoryClosed",
                eventSchemaVersion: 3,
                reviewerRelationship: relationship,
            });
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- NOTE(john): We only want this to run once on mount.
    }, []);

    // @ts-expect-error - TS2339 - Property 'data' does not exist on type 'Result<{ aiGuideThreads: guideTranscripts_aiGuideThreads | null | undefined; }>'.
    const cursorResult = result.data?.aiGuideThreads?.cursor;
    let contents: React.ReactNode = null;

    if (result.status === "loading" || result.status === "no-data") {
        contents = <CircularSpinner />;
    } else if (result.status !== "success") {
        contents = (
            <ErrorMessageWrapper>
                <HeadingMedium style={{textAlign: "center"}}>
                    {t`Oops. Something went wrong. Please try again.`}
                </HeadingMedium>
                <Body
                    style={{
                        textAlign: "center",
                        marginBlockStart: spacing.xLarge_32,
                    }}
                >
                    <ReportError />
                </Body>
            </ErrorMessageWrapper>
        );
    } else {
        if (renderTableView) {
            contents = (
                <TableView
                    threads={result.data?.aiGuideThreads?.threads || []}
                />
            );
        } else if (profilePath) {
            // Classic view needs profile path for thread link
            contents = (
                <ConversationsView
                    threads={result.data?.aiGuideThreads?.threads || []}
                    profilePath={profilePath}
                />
            );
        }
    }

    return (
        <View>
            {profilePath && (
                <Breadcrumbs>
                    <BreadcrumbsItem>
                        <Link href={profilePath}>{t`Profile`}</Link>
                    </BreadcrumbsItem>
                </Breadcrumbs>
            )}
            <HeadingLarge
                style={{
                    marginBlockStart: spacing.xxxSmall_4,
                    marginBlockEnd: spacing.small_12,
                }}
            >
                {t`Khanmigo Chat History`}
            </HeadingLarge>
            <Strut size={spacing.xSmall_8} />
            <Banner
                text={t`Conversations with Khanmigo are retained for up to one year. If flagged for review, they may be retained for up to two years.`}
                layout="full-width"
            />
            <View style={styles.filtersContainer}>
                <DateRangeDropdown
                    onChange={setDateRangeSelection}
                    dateRangeSelection={dateRangeSelection}
                    style={styles.dateRangeDropdown}
                    maxDate={moment()}
                />
                <Strut size={spacing.large_24} />
                <Checkbox
                    label={t`Only show flagged chats`}
                    checked={flaggedThreadsOnly}
                    onChange={setFlaggedThreadsOnly}
                />
            </View>
            <Strut size={spacing.large_24} />
            {contents}
            <Strut size={spacing.medium_16} />
            <View style={{flexDirection: "row"}}>
                {cursor && (
                    <Button href={pathname} kind="tertiary">
                        {t`Most recent conversations`}
                    </Button>
                )}
                <Spring />
                {cursorResult && (
                    <Button href={`?cursor=${cursorResult}`}>
                        {t`View older conversations...`}
                    </Button>
                )}
            </View>
            <Strut size={spacing.medium_16} />
        </View>
    );
};

type ExportProps = WithoutMarkConversion<
    JSX.LibraryManagedAttributes<
        typeof GuideTranscripts,
        React.ComponentProps<typeof GuideTranscripts>
    >
>;

const GuideTranscriptsWithComposed = compose(
    withConversionContext({
        activity: enums.Activity.OTHER_ACTIVITY,
    }),
    withMarkConversion,
)(GuideTranscripts) as React.ComponentType<ExportProps>;
export default GuideTranscriptsWithComposed;

const styles = StyleSheet.create({
    dateRangeDropdown: {
        width: DATE_FILTER_WIDTH,
    },
    filtersContainer: {
        flexDirection: "row",
        marginBlockStart: FILTERS_MARGIN_TOP,
        alignItems: "center",
    },
});
